import React, { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import useScrollReveal from '../hooks/useScrollReveal';
import {
  ArrowRight, Gift, UserPlus, Star, Quote, Target, Eye, Heart, Award, User, Phone, Globe, Calendar, Briefcase,
  FileCheck2, PhoneCall, ShieldCheck, Sparkles, ArrowDown, X, MessageCircleQuestion, Cloud, Languages, BellRing,
  UserCheck, Users, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  NAV_CATEGORIES, WHY_CHOOSE, REVIEWS, EXPERIENCE_STATS,
  LANGUAGES, SOURCES_OF_INCOME,
  defaultSpokenLanguageForUiLocale,
  SERVICE_CATALOG, serviceFormHref,
} from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle, serviceDesc } from '../i18n/serviceCopy';
import {
  buildCampaignCapturePayload,
  getJourneyId,
  trackEvent,
  trackPageLifecycle,
} from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';
import { scrollToAnchor } from '../lib/scrollToAnchor';
import { goToPostSubmit } from '../lib/postSubmitGuest';
import { translateIncomeSource } from '../lib/itrSourceI18n';
import TrustStrip from './TrustStrip';

export const AllServicesGrid = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const catDesc = (cat) => t(`category.${cat.category}_desc`, cat.desc);
  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white to-teal-50/30">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('allsvc.title_all', 'All')} <span className="text-[#14A098]">{t('allsvc.title_services', 'Services')}</span>
          </h2>
          <p className="text-gray-600 mt-8 max-w-2xl mx-auto">{t('allsvc.subtitle', 'Click any service below to apply — our experts will reach out within 2 hours.')}</p>
        </div>
        <div className="space-y-10">
          {NAV_CATEGORIES.map((cat) => (
            <div key={cat.category}>
              <div className="flex flex-wrap items-end justify-between mb-5">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#14A098]">{catLabel(cat)}</div>
                  <div className="text-sm text-gray-500">{catDesc(cat)}</div>
                </div>
                <div className="text-xs text-gray-500">{t('allsvc.services_count', '{n} services', { n: cat.services.length })}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cat.services.map((svc) => (
                  <Link key={svc.code} to={serviceFormHref(svc.code)} className="card-lift group p-5 rounded-2xl border border-gray-100 bg-white">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center mb-3 group-hover:bg-gradient-to-br group-hover:from-[#14A098] group-hover:to-[#5eead4] transition-colors">
                      <svc.icon className="w-5 h-5 text-[#14A098] group-hover:text-white" />
                    </div>
                    <h3 className="font-bold text-[#0f2540] mb-1 leading-snug">{serviceTitle(svc.code, t, svc.title)}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#14A098]">
                      {t('allsvc.fill_form', 'Fill form now')} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TRUST_STAT_ICONS = {
  'Years in Business': Calendar,
  'Happy Clients': Users,
  'Tax Returns Filed': FileCheck2,
  'Business Registrations': Building2,
};

export const TrustBar = () => {
  const { t } = useLanguage();
  const [revealRef, revealed] = useScrollReveal({ threshold: 0.25 });
  const count = EXPERIENCE_STATS.length;
  return (
    <section className="py-16 bg-gradient-to-b from-teal-50/60 via-white to-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[36rem] h-[18rem] bg-[#14A098]/10 rounded-full blur-3xl" />
      <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 shadow-sm">
            <ShieldCheck className="w-3.5 h-3.5 text-[#14A098]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f2540]">
              {t('trust.eyebrow', 'Why India trusts SolveTax')}
            </span>
          </div>
        </div>
        <div ref={revealRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {EXPERIENCE_STATS.map((s, idx) => {
            const Icon = TRUST_STAT_ICONS[s.label] || Award;
            // Staggered right-to-left sweep: the rightmost card animates in first.
            const delayMs = (count - 1 - idx) * 110;
            return (
              <div
                key={s.label}
                className="card-lift text-center rounded-2xl bg-white border border-gray-100 shadow-sm px-4 py-6 transition-all duration-700 ease-out"
                style={{
                  transitionDelay: `${delayMs}ms`,
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? 'translateX(0)' : 'translateX(48px)',
                }}
              >
                <div className="w-11 h-11 mx-auto rounded-xl bg-teal-50 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#14A098]" />
                </div>
                <div className="text-3xl md:text-4xl font-extrabold text-[#0f2540]">{s.value}</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1.5">{t(`trust.stat_${s.label.replace(/\s+/g, '_')}`, s.label)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// GST shows as two direct-service pills (Filing / Registration) instead of the generic
// category pill, with Income Tax centred in the row rather than sitting at either end.
const PLATFORM_PILLS = [
  { kind: 'service', code: 'GST_FILING', labelKey: 'nav.gst_filing', labelFallback: 'GST Filing' },
  { kind: 'service', code: 'GST_REGISTRATION', labelKey: 'nav.gst_registration', labelFallback: 'GST Registration' },
  { kind: 'category', category: 'COMPANY' },
  { kind: 'category', category: 'MCA' },
  { kind: 'category', category: 'INCOME_TAX' },
  { kind: 'category', category: 'TRADEMARK' },
  { kind: 'category', category: 'ACCOUNTING' },
  { kind: 'category', category: 'PAYROLL' },
  { kind: 'category', category: 'LICENSE' },
];

const PLATFORM_CAPABILITIES = [
  { icon: UserCheck, id: 'dedicated_expert', title: 'A dedicated CA for your business', desc: 'One expert stays with your account from day one, tracking every filing — no bouncing between chatbots or support tickets.' },
  { icon: BellRing, id: 'reminders', title: 'Smart deadline reminders', desc: 'Automated nudges for GST, ROC and ITR due dates so you never miss a filing.' },
  { icon: Cloud, id: 'cloud', title: 'Secure cloud document vault', desc: 'Upload once — your PAN, GST and company documents stay safely stored for every future filing.' },
  { icon: Languages, id: 'multilingual', title: 'Support in your language', desc: 'Talk to our experts in Telugu, Hindi or English — whichever is easiest for you.' },
];

export const PlatformFeatures = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const pillItems = PLATFORM_PILLS
    .map((item) => {
      if (item.kind === 'service') {
        return { key: item.code, label: t(item.labelKey, item.labelFallback), href: serviceFormHref(item.code) };
      }
      const cat = NAV_CATEGORIES.find((c) => c.category === item.category);
      if (!cat) return null;
      return { key: cat.category, label: catLabel(cat), href: `/all-services?cat=${encodeURIComponent(cat.category)}` };
    })
    .filter(Boolean);

  return (
    <section className="py-20 bg-[#0f2540] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#14A098]/15 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[24rem] h-[24rem] bg-[#5eead4]/10 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#5eead4]" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#5eead4]">{t('platform.badge', 'A Dedicated Expert, Not Just Automation')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-5 leading-tight">
              {t('platform.title1', 'You focus on your business.')} <span className="text-[#5eead4]">{t('platform.title2', "We'll handle your tax & compliance.")}</span>
            </h2>
            <p className="text-teal-100/80 leading-relaxed mb-4">
              {t('platform.p1', 'A dedicated SolveTax CA tracks every deadline, prepares your filings and gets it right the first time — so nothing slips through the cracks like it can on a generic self-service platform.')}
            </p>
            <p className="text-teal-100/80 leading-relaxed mb-8">
              {t('platform.p2', 'From referral tracking to multi-language support and secure document workflows, we pair real experts with the right technology to make Indian tax and compliance genuinely simple — for individuals and growing businesses alike.')}
            </p>
            <div className="flex flex-wrap gap-2">
              {pillItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 text-white hover:bg-[#14A098] hover:text-white border border-white/10 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {PLATFORM_CAPABILITIES.map((c) => (
              <div key={c.id} className="flex items-start gap-4 rounded-2xl bg-white/[0.06] border border-white/10 p-5 backdrop-blur-sm">
                <div className="w-11 h-11 rounded-xl bg-[#14A098] flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">{t(`platform.cap_${c.id}_title`, c.title)}</h4>
                  <p className="text-sm text-teal-100/70 leading-relaxed">{t(`platform.cap_${c.id}_desc`, c.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const CoreServicesGrid = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const catDesc = (cat) => t(`category.${cat.category}_desc`, cat.desc);
  return (
    <section className="py-20 bg-gradient-to-b from-white to-teal-50/30">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('platform_grid.title1', "SolveTax's trusted platform for")} <span className="text-[#14A098]">{t('platform_grid.title2', 'tax & compliance')}</span>
          </h2>
          <p className="text-gray-600 mt-8 max-w-2xl mx-auto">
            {t('platform_grid.subtitle', 'From return filing to registration and ongoing compliance — everything your business needs, in one place.')}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {NAV_CATEGORIES.map((cat) => {
            const Icon = cat.services[0]?.icon;
            return (
              <Link
                key={cat.category}
                to={`/all-services?cat=${encodeURIComponent(cat.category)}`}
                className="card-lift group p-6 rounded-2xl border border-gray-100 bg-white"
              >
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4 group-hover:bg-gradient-to-br group-hover:from-[#14A098] group-hover:to-[#5eead4] transition-colors">
                  {Icon && <Icon className="w-6 h-6 text-[#14A098] group-hover:text-white" />}
                </div>
                <h3 className="font-bold text-[#0f2540] mb-1.5">{catLabel(cat)}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-3">{catDesc(cat)}</p>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#14A098]">
                  {t('platform_grid.learn_more', 'Learn more')} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FAQ_ITEMS = [
  { id: 'itr', q: 'How does SolveTax help with Income Tax Return filing?', a: 'Share your income details through our form or WhatsApp — a qualified tax expert reviews your case, applies every eligible deduction, and files your ITR within hours of receiving your documents.' },
  { id: 'gst_docs', q: 'What documents do I need for GST registration?', a: 'Typically your PAN, Aadhaar, a passport-size photo, proof of business address and a cancelled cheque or bank statement. Our team tells you exactly what applies to your entity type before you start.' },
  { id: 'company_time', q: 'How long does company or LLP registration take?', a: 'Most Private Limited, LLP and OPC registrations are completed in 7-15 working days once we receive your documents and DSC/DIN formalities are in place.' },
  { id: 'security', q: 'Is my financial data safe with SolveTax?', a: 'Yes. Your documents are stored in a secure cloud vault, accessible only to the experts working on your case, and never shared with third parties.' },
  { id: 'languages', q: 'Can I get support in Telugu or Hindi?', a: 'Absolutely. Pick your preferred language on any of our forms and our team will follow up and support you in Telugu, Hindi or English.' },
];

export const FAQSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-[#14A098]/20 mb-4">
            <MessageCircleQuestion className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('faq.badge', 'Got questions?')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('faq.title', 'Frequently Asked')} <span className="text-[#14A098]">{t('faq.title_accent', 'Questions')}</span>
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ_ITEMS.map((item) => (
            <AccordionItem key={item.id} value={item.id} className="border-gray-100">
              <AccordionTrigger className="text-left text-[#0f2540] font-semibold hover:no-underline hover:text-[#14A098] text-base">
                {t(`faq.${item.id}_q`, item.q)}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed">
                {t(`faq.${item.id}_a`, item.a)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const ABOUT_CARDS = [
  { id: 'mission', icon: Target, title: 'Our Mission', desc: 'Deliver personalised tax solutions that ensure compliance while optimising financial outcomes.' },
  { id: 'vision', icon: Eye, title: 'Our Vision', desc: "Become India's most trusted tax advisory firm known for expertise and integrity." },
  { id: 'values', icon: Heart, title: 'Our Values', desc: 'Integrity, transparency, client-first mindset and continuous learning.' },
  { id: 'promise', icon: Award, title: 'Our Promise', desc: 'Accurate filing, timely delivery and honest advice — every single time.' },
];

export const AboutSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('about.title_about', 'About')} <span className="text-[#14A098]">{t('about.title_brand', 'SolveTax')}</span>
          </h2>
          <p className="text-gray-600 mt-8 mb-6">{t('about.subtitle', 'Your trusted partner for all tax and financial compliance needs')}</p>
          <TrustStrip />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-5 text-gray-700 leading-relaxed">
            <h3 className="text-2xl md:text-3xl font-bold text-[#0f2540] mb-2">{t('about.story_title', 'Our Story')}</h3>
            <p>{t('about.p1', 'Founded with a vision to make taxation simple and accessible, SolveTax has grown into a trusted name in the Indian tax consultancy landscape. We started as a small practice helping individuals file their returns and today serve over 5000 satisfied clients across the country.')}</p>
            <p>{t('about.p2', 'Our team of qualified tax agents, tax experts, and financial advisors bring decades of combined experience to every engagement. We stay ahead of changing tax laws so you do not have to.')}</p>
            <p>{t('about.p3', 'Whether you are a salaried professional filing your ITR, a startup navigating GST complexities, or an established business managing audits — we bring clarity, accuracy, and peace of mind.')}</p>
            <div className="flex flex-wrap gap-3 pt-3">
              <Link to="/about"><Button className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('about.read_full', 'Read Full Story')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/file-itr"><Button variant="outline" className="border-[#14A098] text-[#14A098] rounded-full hover:bg-[#14A098] hover:text-white">{t('about.file_itr_now', 'File ITR Now')}</Button></Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ABOUT_CARDS.map((c) => (
              <div key={c.id} className="card-lift p-6 rounded-2xl bg-teal-50/50 border border-teal-100">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm mb-3"><c.icon className="w-6 h-6 text-[#14A098]" /></div>
                <h4 className="font-bold text-[#0f2540] mb-2">{t(`about.${c.id}_title`, c.title)}</h4>
                <p className="text-sm text-gray-600">{t(`about.${c.id}_desc`, c.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const ReferralSection = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 bg-teal-50/40">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4">
            <Gift className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{t('referral.badge', 'Refer & Earn')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-4">{t('referral.title', 'Our Referral Program')}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">{t('referral.p1', 'At SolveTax, we believe in building relationships through trust and quality service. Our referral program rewards you for sharing your positive experience with others.')}</p>
          <p className="text-gray-600 mb-8 leading-relaxed">{t('referral.p2', 'When you refer a friend or business associate to SolveTax, you will receive special benefits and discounts on our services.')}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/referral"><Button className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('referral.know_more', 'Know More')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/referral#referral-form"><Button variant="outline" className="border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white rounded-full">{t('referral.join', 'Join Referral Program')}</Button></Link>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-[#14A098]/10 to-transparent rounded-[2rem] blur-xl" />
          <div className="relative bg-white rounded-3xl p-10 shadow-xl border border-gray-100 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-lg mb-6">
              <UserPlus className="w-10 h-10 text-white" />
            </div>
            <div className="text-5xl font-extrabold text-[#0f2540]">₹200</div>
            <div className="text-sm uppercase tracking-wider text-[#14A098] font-semibold mt-2">{t('referral.reward_label', 'Reward per referral')}</div>
            <div className="mt-6 text-sm text-gray-500">{t('referral.reward_note', 'Earn every time your referral files a return with us.')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const WhyChoose = () => {
  const { t, locale } = useLanguage();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {locale === 'te' ? (
              <span className="text-[#14A098]">{t('why.title_brand', 'Choose SolveTax')}</span>
            ) : (
              <>
                {t('why.title_why', 'Why')}{' '}
                <span className="text-[#14A098]">{t('why.title_brand', 'Choose SolveTax')}</span>
              </>
            )}
          </h2>
          <p className="text-gray-600 mt-8">{t('why.subtitle', 'What sets us apart from other tax service providers')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_CHOOSE.map((w, i) => (
            <div key={w.title} className="card-lift p-8 rounded-2xl bg-white border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4"><w.icon className="w-7 h-7 text-[#14A098]" /></div>
              <h3 className="font-bold text-[#0f2540] mb-2 text-lg">{t(`why.c${i + 1}_title`, w.title)}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{t(`why.c${i + 1}_desc`, w.desc)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const { t } = useLanguage();
  // Split 10 reviews into 2 rows of 5; each row duplicated for seamless loop
  const row1 = REVIEWS.slice(0, 5);
  const row2 = REVIEWS.slice(5, 10);
  return (
    <section className="py-20 bg-gradient-to-b from-teal-50/40 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] title-underline">
            {t('reviews.title_lead', 'What Our')}{' '}
            <span className="text-[#14A098]">{t('reviews.title_accent', 'Clients Say')}</span>
          </h2>
          <p className="text-gray-600 mt-8">{t('reviews.subtitle', 'Reviews from real Indian taxpayers across the country')}</p>
        </div>

        {/* Row 1 — scrolls right to left */}
        <div className="marquee-mask mb-6">
          <div className="marquee marquee-left">
            {[...row1, ...row1].map((r, idx) => (
              <ReviewCard key={`r1-${r.id}-${idx}`} r={r} />
            ))}
          </div>
        </div>

        {/* Row 2 — scrolls left to right */}
        <div className="marquee-mask">
          <div className="marquee marquee-right">
            {[...row2, ...row2].map((r, idx) => (
              <ReviewCard key={`r2-${r.id}-${idx}`} r={r} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ r }) => (
  <article className="shrink-0 w-[340px] md:w-[380px] p-7 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow relative">
    <Quote className="w-8 h-8 text-[#14A098]/15 absolute top-5 right-5" />
    <div className="flex items-center gap-1 text-amber-400 mb-4">
      {Array.from({ length: r.rating }, (_, i) => i + 1).map((n) => (
        <Star key={`${r.id}-s${n}`} className="w-4 h-4 fill-current" />
      ))}
    </div>
    <p className="text-sm text-gray-700 leading-relaxed mb-5 line-clamp-5" lang={r.langCode}>“{r.quote}”</p>
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center text-white font-bold shrink-0">{r.initials}</div>
      <div className="min-w-0">
        <div className="font-semibold text-[#0f2540] text-sm truncate">{r.name}</div>
        <div className="text-xs text-gray-500 truncate">{r.city} • {r.service}</div>
      </div>
    </div>
  </article>
);

export const CTABand = () => {
  const { t } = useLanguage();
  return (
    <section className="py-16 bg-[#0f2540] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#14A098]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5eead4]/10 rounded-full blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row gap-6 items-center justify-between">
        <div>
          <h3 className="text-white text-2xl md:text-3xl font-extrabold mb-2">{t('cta.title', 'Ready to simplify your taxes?')}</h3>
          <p className="text-teal-100/80">{t('cta.subtitle', 'Get started today and file with confidence in minutes.')}</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center md:justify-end">
          <Link to="/file-itr">
            <Button
              size="lg"
              onClick={() => trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'file_itr',
                page_path: '/home',
              })}
              className="bg-white text-[#0f2540] hover:bg-teal-50 rounded-full px-8 h-12 font-semibold shadow-lg inline-flex items-center gap-2"
            >
              {t('cta.file_itr', 'File ITR')} <ArrowRight className="w-4 h-4 shrink-0" />
            </Button>
          </Link>
          <Link to="/get-started">
            <Button
              size="lg"
              onClick={() => trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'multi_service',
                page_path: '/home',
              })}
              className="bg-white text-[#0f2540] hover:bg-teal-50 rounded-full px-4 sm:px-8 min-h-12 h-auto py-3 font-semibold text-sm sm:text-base whitespace-normal text-center leading-snug shadow-lg inline-flex items-center justify-center gap-2 sm:gap-2.5"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0" aria-hidden />
              <span>{t('cta.multi_service', 'You can request multiple services')}</span>
              <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const HomeSpecialOffer = () => {
  const { t } = useLanguage();
  return (
    <section className="py-8 bg-white">
      <div className="w-full max-w-[1680px] mx-auto px-2 md:px-4">
        <Card className="p-5 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white border-0 shadow-lg">
          <div className="flex items-center gap-2 mb-2"><Gift className="w-5 h-5" /><span className="font-semibold">{t('offer.title', 'Special Offer')}</span></div>
          <p className="text-base md:text-lg text-teal-100 text-center">
            {t('offer.body', 'Submit the form and get a flat 10% discount on your first service with SolveTax.')}
          </p>
        </Card>
      </div>
    </section>
  );
};

const HOME_ITR_INITIAL = {
  client_name: '',
  mobile: '',
  language: '',
  financial_year: '2024-25',
  source_of_income: 'Salary',
};

const HomeItrMiniFormContext = createContext(null);

function useHomeItrMiniForm() {
  const ctx = useContext(HomeItrMiniFormContext);
  if (!ctx) throw new Error('useHomeItrMiniForm must be used within HomeItrMiniFormProvider');
  return ctx;
}

const HomeItrMiniFormProvider = ({ children }) => {
  const { locale } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({
    ...HOME_ITR_INITIAL,
    language: defaultSpokenLanguageForUiLocale(locale),
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const def = defaultSpokenLanguageForUiLocale(locale);
    if (!def) return;
    setForm((prev) => (prev.language ? prev : { ...prev, language: def }));
  }, [locale]);

  const selectedSource = form.source_of_income || 'Salary';

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'ITR_HOME_MINI_FORM',
      form_name: 'HomeIncomeTaxMiniForm',
      service_code: 'ITR_FILING',
      open_event_name: 'itr_home_form_open',
    }),
    [],
  );

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.client_name.trim()) nextErrors.client_name = 'Name is required';
    if (!/^[0-9]{10}$/.test(form.mobile)) nextErrors.mobile = 'Enter valid 10-digit mobile';
    if (!form.language) nextErrors.language = 'Select language';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      trackEvent({
        event_name: 'itr_home_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'HomeIncomeTaxMiniForm',
        funnel_name: 'ITR_HOME_MINI_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
        service_code: 'ITR_FILING',
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      return;
    }
    trackEvent({
      event_name: 'itr_home_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'HomeIncomeTaxMiniForm',
      funnel_name: 'ITR_HOME_MINI_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
      service_code: 'ITR_FILING',
      phone_number: form.mobile,
    });
    const capture = buildCampaignCapturePayload();
    const normalizedSourceOfIncome = form.source_of_income === 'Multiple Sources'
      ? null
      : form.source_of_income;
    const payload = {
      client_name: form.client_name.trim(),
      mobile: form.mobile,
      language: form.language,
      state: null,
      priority: 'NORMAL',
      remarks: null,
      pan_number: null,
      password: null,
      financial_year: form.financial_year,
      filed_status: 'NOT_FILED',
      referral_id: null,
      referral_entity: null,
      email_id: null,
      source_of_income: normalizedSourceOfIncome,
      refund_amount: null,
      rm_id: null,
      op_id: null,
      tag: 'client_website',
      ...capture,
      lead_source: capture.lead_source || 'WEBSITE',
    };

    setSubmitting(true);
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'itr_home_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'HomeIncomeTaxMiniForm',
          funnel_name: 'ITR_HOME_MINI_FORM',
          funnel_step_number: 2,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
          service_code: 'ITR_FILING',
          phone_number: form.mobile,
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          'Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL to frontend/.env (your Apps Script /exec URL), then restart npm.',
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: 'home_itr_mini',
        summary: `${payload.client_name} | ${payload.mobile} | ${payload.source_of_income || '—'} | ITR_FILING`,
        detail: payload,
      });
      trackEvent({
        event_name: 'itr_home_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'HomeIncomeTaxMiniForm',
        funnel_name: 'ITR_HOME_MINI_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_HOME_MINI_FORM', { reset: true }),
        service_code: 'ITR_FILING',
        phone_number: form.mobile,
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success('Submitted successfully. Our team will contact you in 2 hours.');
      goToPostSubmit(navigate, form.client_name);
    } catch (err) {
      trackEvent({
        event_name: 'itr_home_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'HomeIncomeTaxMiniForm',
        funnel_name: 'ITR_HOME_MINI_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('ITR_HOME_MINI_FORM'),
        service_code: 'ITR_FILING',
        phone_number: form.mobile,
        error_code: 'unexpected',
        error_message: String(err?.message || err),
      });
      toast.error(`Could not submit: ${err?.message || 'Unexpected error'}`);
    } finally {
      setSubmitting(false);
    }
  }, [form, navigate]);

  const resetForm = useCallback(() => {
    setForm({ ...HOME_ITR_INITIAL, language: defaultSpokenLanguageForUiLocale(locale) });
    setErrors({});
  }, [locale]);

  const value = useMemo(
    () => ({
      form,
      errors,
      submitting,
      selectedSource,
      setField,
      onSubmit,
      resetForm,
    }),
    [form, errors, submitting, selectedSource, setField, onSubmit, resetForm],
  );

  return (
    <HomeItrMiniFormContext.Provider value={value}>
      {children}
    </HomeItrMiniFormContext.Provider>
  );
};

const HomeIncomeTaxMiniForm = () => {
  const { t } = useLanguage();
  const { form, errors, submitting, setField, onSubmit, resetForm } = useHomeItrMiniForm();

  return (
    <Card id="home-itr-form" className="p-5 md:p-6 rounded-2xl border border-gray-100 shadow-xl bg-white scroll-mt-24">
      <form className="space-y-5" onSubmit={onSubmit}>
        <div className="space-y-3">
          <div>
            <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.full_name', 'Full Name')} <span className="text-rose-500">*</span></Label>
            <Input className="mt-1.5 h-11" placeholder={t('itr.placeholder_name', 'e.g. Rahul Sharma')} value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} />
            {errors.client_name && <p className="text-xs text-rose-500 mt-1">{errors.client_name}</p>}
          </div>
          <div>
            <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.mobile', 'Mobile Number')} <span className="text-rose-500">*</span></Label>
            <Input className="mt-1.5 h-11" placeholder={t('itr.placeholder_mobile', '10-digit mobile')} value={form.mobile} onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} inputMode="numeric" />
            {errors.mobile && <p className="text-xs text-rose-500 mt-1">{errors.mobile}</p>}
          </div>
          <div>
            <Label className="text-sm font-semibold text-[#0f2540] flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#14A098]" />{t('itr.pref_lang', 'Preferred Language')} <span className="text-rose-500">*</span></Label>
            <Select value={form.language} onValueChange={(v) => setField('language', v)}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
            </Select>
            {errors.language && <p className="text-xs text-rose-500 mt-1">{errors.language}</p>}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-0.5">
          <Button type="submit" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-7 h-11 whitespace-normal text-center leading-snug" disabled={submitting}>
            {submitting ? t('itr.submitting', 'Submitting...') : <>{t('form.submit_details', 'Submit Details')} <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
          <Button type="button" variant="outline" className="rounded-full h-11 border-gray-300" onClick={resetForm}>
            {t('itr.reset', 'Reset')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

const HomeItrFilingOptionsCard = () => {
  const { t } = useLanguage();
  const { selectedSource, setField } = useHomeItrMiniForm();

  return (
    <Card className="home-itr-side-card p-5 md:p-6 lg:px-7 rounded-2xl border border-gray-100 shadow-xl bg-white">
      <div className="space-y-3.5">
        <h4 className="text-xl md:text-[1.35rem] lg:text-2xl font-extrabold text-[#0f2540]">{t('itf.filing_opts', 'ITR Filing options')}</h4>
        <div className="flex flex-wrap gap-2">
          {SOURCES_OF_INCOME.map((source) => {
            const active = selectedSource === source;
            return (
              <button
                key={source}
                type="button"
                onClick={() => setField('source_of_income', source)}
                className={`rounded-full px-3.5 py-2 md:px-4 md:py-2 text-sm font-semibold border transition-colors ${
                  active
                    ? 'bg-[#14A098] border-[#14A098] text-white'
                    : 'bg-white border-gray-200 text-[#0f2540] hover:border-[#14A098]/40'
                }`}
              >
                {translateIncomeSource(t, source)}
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export const IncomeTaxHighlightSection = () => {
  const { t } = useLanguage();
  const [showProcessModal, setShowProcessModal] = useState(false);
  const homeItrPromoCta = useMemo(() => {
    const meta = SERVICE_CATALOG.find((s) => s.code === 'ITR_FILING');
    const name = serviceTitle('ITR_FILING', t, meta?.title || 'Income Tax Return Filing');
    return t('common.get_my_service_done', 'Get my {service} done', { service: name });
  }, [t]);
  const filingHowFallbacks = [
    'Fill in your information and submit to get started.',
    'An expert will call you within 2 hours to understand your needs.',
    "Share the required documents—we'll guide you on what's needed.",
    'Our experts prepare and file your Income Tax Return.',
    "Your ITR is filed quickly and you'll receive confirmation.",
  ];
  const quickSteps = filingHowFallbacks.map((fallback, idx) => ({
    id: `step${idx + 1}`,
    label: t(`itr.step${idx + 1}`, `Step ${idx + 1}`),
    icon: [FileCheck2, PhoneCall, Briefcase, ShieldCheck, Sparkles][idx],
    text: t(`itr.filing_how_step${idx + 1}`, fallback),
  }));
  const itrDiscountIntro = t('itr.main_sub_intro', 'Fill the below form get');
  const itrDiscountHighlight = t('itr.main_sub_highlight', '10% Discount');
  const itrFormColumnShell = 'w-full max-w-none sm:max-w-[560px] ml-0';
  const itrFormColumnHeader = (
    <div className={`hidden md:block ${itrFormColumnShell} mb-4 text-center`}>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
        <span className="text-xs font-semibold text-[#0f2540]">{t('itr.badge_line', 'Income Tax Return Filing • AY 2025-26')}</span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <h2 className="ui-title-lg mb-0">{t('itr.main_title', 'Income Tax Return Filing')}</h2>
        <Button
          type="button"
          className="rounded-full h-9 px-4 bg-[#14A098] border-2 border-[#14A098] text-white hover:bg-[#0e7a74] hover:border-[#0e7a74]"
          onClick={() => setShowProcessModal(true)}
        >
          {t('itr.process_btn', 'Process')}
        </Button>
      </div>
    </div>
  );
  const itrDiscountBanner = (
    <div className={`${itrFormColumnShell} mb-3 sm:mb-4 flex flex-col items-center`}>
      <div className="w-full flex justify-center overflow-x-auto">
        <p className="ui-body text-[0.9375rem] sm:text-base leading-snug whitespace-nowrap text-center">
          {itrDiscountIntro}{' '}
          <span className="inline rounded-full bg-[#14A098]/14 border border-[#14A098]/30 px-2.5 py-0.5 font-semibold text-[#0f2540] align-middle">
            {itrDiscountHighlight}
          </span>
        </p>
      </div>
      <a
        href="#home-itr-form"
        className="itr-form-scroll-cue mt-3 group"
        aria-label={t('itr.scroll_to_form', 'Go to the form below')}
        onClick={(e) => {
          e.preventDefault();
          const target = document.getElementById('home-itr-form');
          if (target) {
            scrollToAnchor(target, { behavior: 'smooth' });
            window.history.replaceState(null, '', '#home-itr-form');
            trackEvent({
              event_name: 'cta_click',
              event_action: 'click',
              event_status: 'success',
              event_category: 'navigation',
              cta_name: 'home_itr_scroll_to_form',
              page_path: '/home',
            });
          }
        }}
      >
        <span className="itr-form-scroll-cue__btn" aria-hidden>
          <ArrowDown className="w-5 h-5" strokeWidth={2.5} />
        </span>
      </a>
    </div>
  );
  return (
    <section className="py-8 hero-gradient">
      <div className="w-full max-w-[1680px] mx-auto px-2 sm:px-3 md:px-5 lg:px-6">
        {/* App view: show this header first (above Benefits) */}
        <div className="md:hidden text-center mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4 shadow-sm">
            <span className="text-xs font-semibold text-[#0f2540]">{t('itr.badge_line', 'Income Tax Return Filing • AY 2025-26')}</span>
          </div>
          <div className="mb-3 flex items-center justify-center gap-2">
            <h2 className="ui-title-lg mb-0">{t('itr.main_title', 'Income Tax Return Filing')}</h2>
            <Button
              type="button"
              onClick={() => setShowProcessModal(true)}
              className="rounded-full h-9 px-4 bg-[#14A098] border-2 border-[#14A098] text-white hover:bg-[#0e7a74] hover:border-[#0e7a74]"
            >
              {t('itr.process_btn', 'Process')}
            </Button>
          </div>
          <a
            href="#home-itr-form"
            onClick={() =>
              trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'home_mobile_fill_form_get_itr',
                page_path: '/home',
              })
            }
            className="mt-4 inline-flex w-full max-w-[360px] items-center justify-center gap-2 rounded-full border-2 border-[#14A098] bg-[#14A098] px-5 h-11 text-sm font-semibold text-white shadow-md shadow-[#14A098]/35 transition-colors hover:bg-[#0e7a74] hover:border-[#0e7a74] active:bg-[#0c6b66] active:border-[#0c6b66] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098] focus-visible:ring-offset-2 whitespace-normal text-center leading-snug"
          >
            {homeItrPromoCta}
            <ArrowRight className="h-4 w-4 shrink-0 opacity-95" aria-hidden />
          </a>
        </div>
        {showProcessModal && (
          <div
            className="fixed inset-0 z-50 bg-black/45 p-3 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-quick-process-title"
            onClick={() => setShowProcessModal(false)}
          >
            <div
              className="mx-auto mt-6 w-full max-w-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="p-4 rounded-2xl border-2 border-[#14A098]/40 bg-white shadow-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14A098]/10 border border-[#14A098]/25">
                    <span className="w-2 h-2 rounded-full bg-[#14A098]" />
                    <span id="home-quick-process-title" className="text-xs font-semibold uppercase tracking-wider text-[#0f2540]">{t('itr.quick_process', 'Quick Process')}</span>
                  </div>
                  <button type="button" onClick={() => setShowProcessModal(false)} className="w-8 h-8 rounded-full border border-gray-200 inline-flex items-center justify-center text-[#0f2540]" aria-label={t('common.close', 'Close')}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  {quickSteps.map((item, idx) => (
                    <div key={item.id} className={`rounded-xl border shadow-md p-3 text-white ${idx % 2 === 0 ? 'bg-gradient-to-r from-[#0f7f79] to-[#14A098] border-[#14A098]' : 'bg-gradient-to-r from-cyan-500 to-teal-500 border-cyan-500'}`}>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 mb-1">{item.label}</div>
                      <div className="flex items-start gap-2 justify-between">
                        <div className="flex items-start gap-2 min-w-0">
                          <item.icon className="w-4 h-4 text-white shrink-0 mt-0.5" />
                          <span className="font-semibold leading-snug">{item.text}</span>
                        </div>
                        <ArrowDown className="w-4 h-4 text-white shrink-0 opacity-90" strokeWidth={2.5} />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}

        <HomeItrMiniFormProvider>
        <div className="grid md:grid-cols-12 gap-5 md:gap-4 lg:gap-6 items-start">
          <div className="order-1 md:order-1 md:col-span-5 lg:col-span-5 space-y-4 w-full min-w-0 mx-auto md:mx-0 md:max-w-none self-start">
            <Card className="home-itr-side-card rotating-border-soft p-5 md:p-6 lg:px-7 rounded-2xl shadow-md shadow-[#14A098]/15">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{t('itr.benefits_label', 'Benefits')}</p>
              <div className="mt-3 flex gap-3 rounded-xl border border-[#14A098]/22 bg-gradient-to-br from-teal-50/90 via-white to-white p-3.5 sm:p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
                <div
                  className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#14A098] text-white shadow-md shadow-[#14A098]/30"
                  aria-hidden
                >
                  <Sparkles className="h-5 w-5" strokeWidth={2} />
                </div>
                <p className="min-w-0 text-[1.22rem] sm:text-[1.45rem] font-extrabold leading-snug tracking-tight text-[#0f2540] text-balance">
                  {t('itr.benefits_line', 'File Your ITR and Get Maximum Refund')}
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-emerald-100">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#14A098] flex items-center justify-center shadow-[0_4px_14px_rgba(20,160,152,0.35)]"
                    aria-hidden
                  >
                    <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                      <span className="text-2xl sm:text-[1.65rem] font-extrabold text-[#0f2540] tracking-tight tabular-nums">
                        ₹200
                      </span>
                      <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-[#14A098]">
                        {t('referral.reward_label', 'Reward per referral')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-snug">
                      {t(
                        'itr.benefit_referral_line',
                        'Get ₹200 on every successful ITR referral when they file their return with us.',
                      )}
                    </p>
                  </div>
                </div>
                <Link
                  to="/referral#referral-form"
                  onClick={() =>
                    trackEvent({
                      event_name: 'cta_click',
                      event_action: 'click',
                      event_status: 'success',
                      event_category: 'navigation',
                      cta_name: 'benefits_submit_referral',
                      page_path: '/home',
                    })
                  }
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#14A098] bg-white px-4 py-2.5 text-sm font-semibold text-[#14A098] shadow-sm transition-colors hover:bg-[#14A098] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098]/35 focus-visible:ring-offset-2"
                >
                  <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                  {t('nav.submit_referral', 'Submit Referral')}
                </Link>
              </div>
            </Card>
            <div className="hidden md:block">
              <HomeItrFilingOptionsCard />
            </div>
          </div>

          <div className="order-2 md:order-2 md:col-span-4 lg:col-span-4 w-full min-w-0 max-w-[860px] mx-auto md:mx-0 md:max-w-none self-start">
            <div className="w-full max-w-none sm:max-w-[520px] md:max-w-none ml-0 space-y-4 md:space-y-5">
              {itrFormColumnHeader}
              {itrDiscountBanner}
              <HomeIncomeTaxMiniForm />
            </div>
          </div>

          <div className="order-3 md:hidden w-full min-w-0 mx-auto max-w-[860px]">
            <HomeItrFilingOptionsCard />
          </div>

          <div
            className="hidden md:flex md:order-3 md:col-span-3 lg:col-span-3 relative w-full justify-center items-end self-end overflow-visible"
            aria-hidden
          >
            <div className="home-itr-hero-visual">
              <img
                src="/itr-hero-person.png?v=3"
                alt=""
                className="home-itr-hero-visual__img"
                width={738}
                height={1024}
                loading="eager"
                decoding="async"
                fetchPriority="low"
              />
            </div>
          </div>
        </div>
        </HomeItrMiniFormProvider>
      </div>
    </section>
  );
};
